// Third party
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';

// Components
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { TCustomTheme } from '@mgm/common/theme';
import Chart, { ApexOptions } from '@mgm/common/components/charts';

// Data
import { BaseTooltip } from '@mgm/common/components/base';
import { convertToCamelCase } from '@mgm/common/utils';
import {
  expensesChartOptions,
  ordersChartOptions,
  salesChartOptions,
} from '../data/dashboard';

// Types
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDashboardData } from '../../../store/dashboard/selectors';
import { getCurrencyByCode } from '../../../store/common/selectors';

const DashboardExpenses = () => {
  const { t } = useTranslation(['common', 'dashboard']);
  const { orders } = useAppSelector(getDashboardData);
  const theme = useTheme() as TCustomTheme;
  const getCurrency = useAppSelector(getCurrencyByCode);

  const expensesItems = useMemo(() => [
    {
      title: t(`${convertToCamelCase(orders?.salesWidget?.title)}`, { ns: 'dashboard' }),
      chartData: [{ data: [...(orders?.salesWidget?.chartData?.[0]?.data || [])] }],
      value: `${orders?.salesWidget?.value?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${getCurrency().mark}`,
      tooltip: t('sumOfAllOrdersSubtotals', { ns: 'dashboard' }),
      chartOptions: salesChartOptions(theme, getCurrency().mark),
    },
    {
      title: t(`${convertToCamelCase(orders?.ordersWidget?.title)}`),
      chartOptions: ordersChartOptions(theme),
      chartData: [{ data: [...(orders?.ordersWidget?.chartData?.[0]?.data || [])] }],
      value: orders?.ordersWidget?.value,
      tooltip: t('evaluatingOrdersWithFinancialStatusOf', { ns: 'dashboard' }),
    },
    {
      title: t(`${convertToCamelCase(orders?.productsWidget?.title)}`),
      chartOptions: expensesChartOptions(theme),
      chartData: [{ data: [...(orders?.productsWidget?.chartData?.[0]?.data || [])] }],
      value: orders?.productsWidget?.value,
      tooltip: t(`${convertToCamelCase(orders?.productsWidget?.tooltip)}`, { ns: 'dashboard' }),
    },
  ], [orders]);

  return (
    <Grid container spacing={2}>
      {
        expensesItems?.map((item) => (
          <Grid
            key={item.title}
            size={{
              lg: 4,
              md: 4,
              xs: 12,
              sm: 12,
            }}
          >
            <Card sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2">{item.title}</Typography>
                <BaseTooltip tooltipText={item.tooltip}>
                  <Typography variant="h3" gutterBottom>{Number(item.value) !== 0 ? item.value : 'N/A'}</Typography>
                </BaseTooltip>
              </Box>

              <Chart
                type="bar"
                series={[...item.chartData]}
                options={item.chartOptions as unknown as ApexOptions}
                width={100}
                height={60}
              />
            </Card>
          </Grid>
        ))
      }
    </Grid>
  );
};

export {
  DashboardExpenses,
};
